import React, { useMemo, useState } from "react";
import {
  Alert,
  Box,
  Button,
  Chip,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  FormControl,
  IconButton,
  InputLabel,
  MenuItem,
  Paper,
  Select,
  Switch,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TextField,
  Typography,
} from "@mui/material";
import AddIcon from "@mui/icons-material/Add";
import DeleteIcon from "@mui/icons-material/Delete";
import EditIcon from "@mui/icons-material/Edit";
import {
  useLandlordPayoutNotificationRules,
  useNotificationAdminConfigs,
  useNotificationTemplates,
} from "services/hooks/rentals";
import {
  CHANNEL_OPTIONS,
  PAYOUT_NOTIFICATION_TYPE_OPTIONS,
  RECIPIENT_TARGET_OPTIONS,
  REPORT_PERIOD_OPTIONS,
  isAdminPayoutTextTemplate,
  isLandlordPayoutDocumentTemplate,
} from "../constants";

const isLandlordRecipient = (target) => target === "LANDLORD";

const PayoutRulesTab = () => {
  const { templates, templateActionError } = useNotificationTemplates();
  const { adminConfigs, adminConfigActionError } = useNotificationAdminConfigs();
  const {
    payoutRules,
    isLoadingPayoutRules,
    errorPayoutRules,
    isSubmittingPayoutRule,
    payoutRuleActionError,
    createPayoutRule,
    updatePayoutRule,
    deletePayoutRule,
  } = useLandlordPayoutNotificationRules();
  const [message, setMessage] = useState({ type: "", text: "" });
  const [dialogOpen, setDialogOpen] = useState(false);
  const [editingId, setEditingId] = useState(null);
  const [form, setForm] = useState({
    related_organization: "",
    name: "",
    notification_type: "AFTER_PAYDAY",
    day_offset: 0,
    recipient_target: "ADMIN",
    report_period: "PREVIOUS_MONTH",
    channel: "EMAIL",
    related_template: "",
    related_admin_config: "",
    is_active: true,
  });

  const isLandlordRule = isLandlordRecipient(form.recipient_target);

  const templateOptions = useMemo(() => {
    const pool = isLandlordRule
      ? templates.filter(isLandlordPayoutDocumentTemplate)
      : templates.filter(isAdminPayoutTextTemplate);
    return pool.map((template) => ({
      id: template.id,
      label: template.name,
      delivery_mode: template.delivery_mode || "TEXT",
      channel: template.channel,
    }));
  }, [templates, isLandlordRule]);

  const adminConfigOptions = useMemo(
    () => adminConfigs.map((config) => ({ id: config.id, label: config.name })),
    [adminConfigs]
  );

  const selectedNotificationType = PAYOUT_NOTIFICATION_TYPE_OPTIONS.find(
    (option) => option.value === form.notification_type
  );
  const selectedRecipient = RECIPIENT_TARGET_OPTIONS.find(
    (option) => option.value === form.recipient_target
  );
  const selectedReportPeriod = REPORT_PERIOD_OPTIONS.find(
    (option) => option.value === form.report_period
  );

  const notificationTypeLabelMap = PAYOUT_NOTIFICATION_TYPE_OPTIONS.reduce((accumulator, option) => {
    accumulator[option.value] = option.label;
    return accumulator;
  }, {});

  const recipientLabelMap = RECIPIENT_TARGET_OPTIONS.reduce((accumulator, option) => {
    accumulator[option.value] = option.label;
    return accumulator;
  }, {});

  const defaultForm = (overrides = {}) => ({
    related_organization: "",
    name: "",
    notification_type: "AFTER_PAYDAY",
    day_offset: 0,
    recipient_target: "ADMIN",
    report_period: "PREVIOUS_MONTH",
    channel: "EMAIL",
    related_template: "",
    related_admin_config: "",
    is_active: true,
    ...overrides,
  });

  const handleRecipientChange = (recipient_target) => {
    setForm((prev) => ({
      ...prev,
      recipient_target,
      channel: "EMAIL",
      related_template: "",
      related_admin_config: recipient_target === "LANDLORD" ? "" : prev.related_admin_config,
      report_period: prev.report_period || "PREVIOUS_MONTH",
    }));
  };

  const handleOpenCreate = () => {
    setEditingId(null);
    setForm(defaultForm());
    setDialogOpen(true);
  };

  const handleOpenEdit = (row) => {
    setEditingId(row.id);
    const recipient = row.recipient_target || "ADMIN";
    setForm({
      related_organization: row.related_organization ?? "",
      name: row.name || "",
      notification_type: row.notification_type || "AFTER_PAYDAY",
      day_offset: row.day_offset ?? 0,
      recipient_target: recipient,
      report_period: row.report_period || "PREVIOUS_MONTH",
      channel: recipient === "LANDLORD" ? "EMAIL" : row.channel || "EMAIL",
      related_template: row.related_template?.id ?? row.related_template ?? "",
      related_admin_config: row.related_admin_config?.id ?? row.related_admin_config ?? "",
      is_active: row.is_active !== false,
    });
    setDialogOpen(true);
  };

  const handleSave = async () => {
    if (!form.name.trim() || !form.related_template) {
      setMessage({ type: "error", text: "Name and template are required." });
      return;
    }
    if (!isLandlordRule && !form.related_admin_config) {
      setMessage({ type: "error", text: "Admin config is required for staff alert rules." });
      return;
    }
    if (isLandlordRule && form.channel !== "EMAIL") {
      setMessage({ type: "error", text: "Landlord report rules must use the EMAIL channel." });
      return;
    }

    const selectedTemplate = templates.find((t) => t.id === Number(form.related_template));
    if (isLandlordRule && !isLandlordPayoutDocumentTemplate(selectedTemplate)) {
      setMessage({
        type: "error",
        text: "Landlord rules require a scheduled DOCUMENT + EMAIL template (no event key).",
      });
      return;
    }

    const payload = {
      related_organization: form.related_organization ? Number(form.related_organization) : null,
      name: form.name.trim(),
      notification_type: form.notification_type,
      day_offset: Number(form.day_offset),
      recipient_target: form.recipient_target,
      report_period: form.report_period || "PREVIOUS_MONTH",
      channel: isLandlordRule ? "EMAIL" : form.channel,
      related_template: Number(form.related_template),
      related_admin_config: isLandlordRule
        ? null
        : form.related_admin_config
        ? Number(form.related_admin_config)
        : null,
      is_active: form.is_active,
    };

    const response = editingId
      ? await updatePayoutRule(editingId, payload)
      : await createPayoutRule(payload);
    if (response.success) {
      setMessage({
        type: "success",
        text: editingId ? "Payout rule updated successfully." : "Payout rule created successfully.",
      });
      setDialogOpen(false);
    } else {
      setMessage({ type: "error", text: response.message || "Failed to save payout rule." });
    }
  };

  const handleDelete = async (id) => {
    if (!window.confirm("Delete this payout rule?")) return;
    const response = await deletePayoutRule(id);
    if (response.success) {
      setMessage({ type: "success", text: "Payout rule deleted." });
    } else {
      setMessage({ type: "error", text: response.message || "Failed to delete payout rule." });
    }
  };

  const dayOffsetHelper =
    form.notification_type === "AFTER_PAYDAY" && Number(form.day_offset) === 0
      ? "0 = same day as pay day (on pay day)."
      : form.notification_type === "ON_PAYDAY"
      ? "On payday — offset is usually 0."
      : undefined;

  return (
    <Box>
      <Box display="flex" justifyContent="space-between" alignItems="center" mb={2}>
        <Typography variant="h6">Landlord Payout Rules</Typography>
        <Button variant="contained" startIcon={<AddIcon />} onClick={handleOpenCreate}>
          Add Payout Rule
        </Button>
      </Box>

      <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
        Staff alerts email admins via an admin config. Landlord rules email each landlord on pay day with a PDF
        property report — create a <strong>DOCUMENT</strong> template under Scheduled Templates first.
      </Typography>

      {message.text && (
        <Alert severity={message.type === "error" ? "error" : "success"} sx={{ mb: 2 }} onClose={() => setMessage({ type: "", text: "" })}>
          {message.text}
        </Alert>
      )}

      {errorPayoutRules && (
        <Alert severity="error" sx={{ mb: 2 }}>
          Failed to load payout rules.
        </Alert>
      )}

      {(payoutRuleActionError || templateActionError || adminConfigActionError) && (
        <Alert severity="error" sx={{ mb: 2 }}>
          {payoutRuleActionError || templateActionError || adminConfigActionError}
        </Alert>
      )}

      <TableContainer component={Paper}>
        {isLoadingPayoutRules ? (
          <Box p={4} display="flex" justifyContent="center">
            <CircularProgress />
          </Box>
        ) : (
          <Table size="small">
            <TableHead>
              <TableRow>
                <TableCell>ID</TableCell>
                <TableCell>Name</TableCell>
                <TableCell>Recipient</TableCell>
                <TableCell>Report period</TableCell>
                <TableCell>Type</TableCell>
                <TableCell>Offset</TableCell>
                <TableCell>Channel</TableCell>
                <TableCell>Template</TableCell>
                <TableCell>Delivery</TableCell>
                <TableCell>Admin Config</TableCell>
                <TableCell>Active</TableCell>
                <TableCell align="right">Actions</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {payoutRules.length === 0 ? (
                <TableRow>
                  <TableCell colSpan={12}>No payout rules found.</TableCell>
                </TableRow>
              ) : (
                payoutRules.map((row) => (
                  <TableRow key={row.id}>
                    <TableCell>{row.id}</TableCell>
                    <TableCell>{row.name || "—"}</TableCell>
                    <TableCell>{recipientLabelMap[row.recipient_target] || row.recipient_target || "ADMIN"}</TableCell>
                    <TableCell>
                      {row.recipient_target === "LANDLORD" ? row.report_period || "—" : "—"}
                    </TableCell>
                    <TableCell>{notificationTypeLabelMap[row.notification_type] || row.notification_type || "—"}</TableCell>
                    <TableCell>{row.day_offset ?? 0}</TableCell>
                    <TableCell>{row.channel || "—"}</TableCell>
                    <TableCell>{row.related_template?.name || row.related_template?.id || "—"}</TableCell>
                    <TableCell>
                      {row.related_template?.delivery_mode ? (
                        <Chip size="small" label={row.related_template.delivery_mode} variant="outlined" />
                      ) : (
                        "—"
                      )}
                    </TableCell>
                    <TableCell>{row.related_admin_config?.name || row.related_admin_config?.id || "—"}</TableCell>
                    <TableCell>{row.is_active ? "Yes" : "No"}</TableCell>
                    <TableCell align="right">
                      <IconButton size="small" onClick={() => handleOpenEdit(row)}>
                        <EditIcon />
                      </IconButton>
                      <IconButton size="small" color="error" onClick={() => handleDelete(row.id)}>
                        <DeleteIcon />
                      </IconButton>
                    </TableCell>
                  </TableRow>
                ))
              )}
            </TableBody>
          </Table>
        )}
      </TableContainer>

      <Dialog open={dialogOpen} onClose={() => setDialogOpen(false)} fullWidth maxWidth="md">
        <DialogTitle>{editingId ? "Edit Payout Rule" : "Create Payout Rule"}</DialogTitle>
        <DialogContent>
          <FormControl fullWidth margin="normal">
            <InputLabel>Recipient</InputLabel>
            <Select
              label="Recipient"
              value={form.recipient_target}
              onChange={(event) => handleRecipientChange(event.target.value)}
            >
              {RECIPIENT_TARGET_OPTIONS.map((option) => (
                <MenuItem key={option.value} value={option.value}>
                  <Box>
                    <Typography variant="body2">{option.label}</Typography>
                    <Typography variant="caption" color="text.secondary">
                      {option.description}
                    </Typography>
                  </Box>
                </MenuItem>
              ))}
            </Select>
          </FormControl>
          {selectedRecipient?.description && (
            <Typography variant="caption" color="text.secondary" display="block">
              {selectedRecipient.description}
            </Typography>
          )}

          {isLandlordRule && (
            <FormControl fullWidth margin="normal">
              <InputLabel>Report period</InputLabel>
              <Select
                label="Report period"
                value={form.report_period}
                onChange={(event) => setForm((prev) => ({ ...prev, report_period: event.target.value }))}
              >
                {REPORT_PERIOD_OPTIONS.map((option) => (
                  <MenuItem key={option.value} value={option.value}>
                    <Box>
                      <Typography variant="body2">{option.label}</Typography>
                      <Typography variant="caption" color="text.secondary">
                        {option.description}
                      </Typography>
                    </Box>
                  </MenuItem>
                ))}
              </Select>
            </FormControl>
          )}
          {isLandlordRule && selectedReportPeriod?.description && (
            <Typography variant="caption" color="text.secondary" display="block" sx={{ mb: 1 }}>
              {selectedReportPeriod.description}
            </Typography>
          )}

          <TextField
            fullWidth
            margin="normal"
            label="Organisation ID (optional for global)"
            value={form.related_organization}
            onChange={(event) => setForm((prev) => ({ ...prev, related_organization: event.target.value }))}
          />
          <TextField
            fullWidth
            margin="normal"
            label="Rule Name"
            value={form.name}
            onChange={(event) => setForm((prev) => ({ ...prev, name: event.target.value }))}
            placeholder={
              isLandlordRule ? "Landlord monthly report on pay day" : "Staff alert: landlords due today"
            }
          />
          <FormControl fullWidth margin="normal">
            <InputLabel>Notification Type</InputLabel>
            <Select
              label="Notification Type"
              value={form.notification_type}
              onChange={(event) => setForm((prev) => ({ ...prev, notification_type: event.target.value }))}
            >
              {PAYOUT_NOTIFICATION_TYPE_OPTIONS.map((option) => (
                <MenuItem key={option.value} value={option.value}>
                  <Box>
                    <Typography variant="body2">{option.label}</Typography>
                    <Typography variant="caption" color="text.secondary">
                      {option.description}
                    </Typography>
                  </Box>
                </MenuItem>
              ))}
            </Select>
          </FormControl>
          <Typography variant="caption" color="text.secondary">
            {selectedNotificationType?.description}
          </Typography>
          <TextField
            fullWidth
            type="number"
            margin="normal"
            label="Day Offset"
            value={form.day_offset}
            onChange={(event) => setForm((prev) => ({ ...prev, day_offset: event.target.value }))}
            helperText={dayOffsetHelper}
          />
          <FormControl fullWidth margin="normal">
            <InputLabel>Channel</InputLabel>
            <Select
              label="Channel"
              value={isLandlordRule ? "EMAIL" : form.channel}
              onChange={(event) => setForm((prev) => ({ ...prev, channel: event.target.value }))}
              disabled={isLandlordRule}
            >
              {(isLandlordRule ? ["EMAIL"] : CHANNEL_OPTIONS).map((option) => (
                <MenuItem key={option} value={option}>
                  {option}
                </MenuItem>
              ))}
            </Select>
          </FormControl>
          <FormControl fullWidth margin="normal" required>
            <InputLabel>Template</InputLabel>
            <Select
              label="Template"
              value={form.related_template}
              onChange={(event) => setForm((prev) => ({ ...prev, related_template: event.target.value }))}
            >
              {templateOptions.length === 0 ? (
                <MenuItem disabled value="">
                  {isLandlordRule
                    ? "No DOCUMENT email templates — create one under Templates"
                    : "No TEXT scheduled templates available"}
                </MenuItem>
              ) : (
                templateOptions.map((option) => (
                  <MenuItem key={option.id} value={option.id}>
                    <Box display="flex" alignItems="center" gap={1} flexWrap="wrap">
                      <Typography variant="body2">{option.label}</Typography>
                      <Chip size="small" label={option.delivery_mode} variant="outlined" />
                      <Typography variant="caption" color="text.secondary">
                        {option.channel}
                      </Typography>
                    </Box>
                  </MenuItem>
                ))
              )}
            </Select>
          </FormControl>
          {!isLandlordRule && (
            <FormControl fullWidth margin="normal" required>
              <InputLabel>Admin Config</InputLabel>
              <Select
                label="Admin Config"
                value={form.related_admin_config}
                onChange={(event) =>
                  setForm((prev) => ({ ...prev, related_admin_config: event.target.value }))
                }
              >
                {adminConfigOptions.map((option) => (
                  <MenuItem key={option.id} value={option.id}>
                    {option.label}
                  </MenuItem>
                ))}
              </Select>
            </FormControl>
          )}
          {isLandlordRule && (
            <Alert severity="info" sx={{ mt: 1 }}>
              Admin config is not used for landlord report rules. Ensure landlords have{" "}
              <code>related_user.email</code> set or sends are skipped.
            </Alert>
          )}
          <Box display="flex" alignItems="center" mt={1}>
            <Switch
              checked={form.is_active}
              onChange={(event) => setForm((prev) => ({ ...prev, is_active: event.target.checked }))}
            />
            <Typography variant="body2">Active — turn on when ready to go live</Typography>
          </Box>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setDialogOpen(false)}>Cancel</Button>
          <Button variant="contained" onClick={handleSave} disabled={isSubmittingPayoutRule}>
            {isSubmittingPayoutRule ? <CircularProgress size={24} /> : editingId ? "Update" : "Create"}
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};

export default PayoutRulesTab;
